import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import authApi, { User, UserRole } from '../services/authApi';
import { tokenStorage, USER_KEY } from '../services/httpClient';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(loadPersistedUser);
  const [isBootstrapping, setIsBootstrapping] = useState(
    !!tokenStorage.getAccess()
  );

  const persistUser = useCallback((u: User | null) => {
    if (u) {
      localStorage.setItem(USER_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setUser(u);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const pair = await authApi.login(email, password);
      tokenStorage.set(pair.access_token, pair.refresh_token);
      persistUser(pair.user);
      window.dispatchEvent(new Event('auth:token-changed'));
    },
    [persistUser]
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    tokenStorage.clear();
    persistUser(null);
    window.dispatchEvent(new Event('auth:token-changed'));
  }, [persistUser]);

  const refreshUser = useCallback(async () => {
    if (!tokenStorage.getAccess()) {
      persistUser(null);
      return;
    }
    try {
      const me = await authApi.me();
      persistUser(me);
    } catch {
      tokenStorage.clear();
      persistUser(null);
    }
  }, [persistUser]);

  // All'avvio, se abbiamo un access token, verifichiamo che sia ancora valido.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (tokenStorage.getAccess()) {
        try {
          const me = await authApi.me();
          if (!cancelled) persistUser(me);
        } catch {
          if (!cancelled) {
            tokenStorage.clear();
            persistUser(null);
          }
        }
      }
      if (!cancelled) setIsBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Evento globale dall'interceptor httpClient su 401 definitivo
  useEffect(() => {
    const onUnauthorized = () => {
      persistUser(null);
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [persistUser]);

  const hasRole = useCallback(
    (...roles: UserRole[]) => !!user && roles.includes(user.role),
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isBootstrapping,
      login,
      logout,
      refreshUser,
      hasRole,
    }),
    [user, isBootstrapping, login, logout, refreshUser, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export default AuthContext;
